import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import { useTheme } from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { ListingPage } from 'foundations-components/transitional/components/ListingPage';
import { FORECAST_CATEGORY } from '../constants/DealGroupingType';

// @ts-expect-error untyped
import ProvideReferenceResolvers from 'reference-resolvers/ProvideReferenceResolvers';
import { DEAL_PROPERTY, DEAL_PIPELINE, DEAL_PIPELINE_STAGE
// @ts-expect-error untyped
} from 'reference-resolvers/constants/ReferenceObjectTypes';
// @ts-expect-error untyped
import DealPropertyReferenceResolver from 'reference-resolvers/resolvers/DealPropertyReferenceResolver';
// @ts-expect-error untyped
import PipelineStageReferenceResolver from 'reference-resolvers/resolvers/PipelineStageReferenceResolver';
// @ts-expect-error untyped
import PipelineReferenceResolver from 'reference-resolvers/resolvers/PipelineReferenceResolver';
import ForecastHeader from '../components/headers/ForecastHeader';
import RepOverviewTableContainer from '../components/RepOverviewTableContainer';
import FrameworkDealTable from '../components/frameworkDealTable/FrameworkDealTable';
import ForecastFilters from '../components/filters/ForecastFilters';
import DealTableFilters from '../components/DealTableFilters';
import CategoryForecastingSetup from '../components/categoryForecasting/CategoryForecastingSetup';
import ProcessingAlert from '../components/alerts/ProcessingAlert';
import { CreateForecastTypeWizardWrapper } from '../components/fmo/CreateForecastTypeWizardWrapper';
import RepDetailForecastSubmissionPanelWrapper from '../components/repDetail/RepDetailForecastSubmissionPanelWrapper';
import { trackRepDetailView, createOnTrackHandlerForSubmissionPanel } from '../utils/usageTracker';
import { fetchAggregatedOverviewData, fetchInitialOverviewData } from '../actions/AggregatedOverviewDataActions';
import { setDetailViewOwner } from '../actions/DetailViewActions';
import { fetchPortalWideCadence } from '../actions/ForecastActions';
import { updateForecastDisplayFromEmbeddedSubmission } from '../actions/ForecastActions';
import { fetchCurrencyInfoAction } from '../actions/MultiCurrencyActions';
import { getOwnerIdsWithPermissions, getOwnerIdToUserIdMap, getOwnersMap, getFetchOwnersSucceeded } from '../selectors/ownersSelectors';
import { getCurrentUser, getHasViewAllScope, getHasViewTeamScope, getIsUngatedToManualSubmissionOverhaul } from '../selectors/authSelectors';
import { getFetchUsersSucceeded } from '../selectors/usersSelectors';
import { getDealGroupingTypeFilter, getPipelineFilter, getTimePeriodFilter } from '../selectors/filtersSelectors';
import { getShowForecastCategoryWizard } from '../selectors/setupSelectors';
import { getShowProcessingAlert, getShouldShowGoalsBugAlert } from '../selectors/alertsSelectors';
import { getIsOverviewDataFetching, getIsInitialOverviewFetchSuccessful } from '../selectors/overviewSelectors';
import { getHasAggregatedOverviewFetchError, getRepRowData } from '../selectors/aggregatedOverviewDataSelectors';
import { getIsAllPipelines, getPortalWideCadence, getUserHasAccessToNoForecastTypes } from '../selectors/forecastSelectors';
import { ForecastTypePermissionError } from '../components/fmo/ForecastTypePermissionError';
import { getIsDealStageView, getSelectedForecastTypeIsDefault } from '../selectors/forecastSelectors';
import { getOwnerIdFromURL, getPermissionedOwnerFromURL } from '../utils/userUtils';
import { getCompanyCurrency } from '../selectors/multiCurrencySelectors';
import useFetchPreferredCurrency from '../components/multicurrency/hooks/useFetchPreferredCurrency';
import { getSelectedDealCount, getSelectedDealIds } from '../selectors/dealsSelectors';
import { isTimePeriodCustom } from '../utils/dateUtils';
import { ALL_PIPELINES, MULTI_BUT_NOT_ALL_PIPELINES } from '../constants/Pipelines';
import { CUSTOM_DATE_OPTION } from '../constants/FiltersConstants';
import { View } from 'foundations-components/components/View';
import GoalsBugAlert from '../components/alerts/GoalsBugAlert';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RepDetailWrapper = ({
  navMarkerLabel,
  children
}) => {
  const theme = useTheme();
  return /*#__PURE__*/_jsx(NavMarker, {
    name: navMarkerLabel,
    children: /*#__PURE__*/_jsx(ListingPage, {
      "data-test-id": "rep-detail-container",
      mainSectionBackgroundColor: theme.color['fill-surface-raised'],
      pageLayout: "full-width",
      headerComponent: /*#__PURE__*/_jsx(ForecastHeader, {
        isDetail: true
      }),
      children: children
    })
  });
};
const RepDetailContainer = () => {
  var _useSelector$length, _useSelector, _owner$get;
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  // Alerts and Wizards
  const showProcessingAlert = useSelector(getShowProcessingAlert);
  const showForecastCategoryWizard = useSelector(getShowForecastCategoryWizard);

  // Permissions
  const hasViewAllScope = useSelector(getHasViewAllScope);
  const hasViewTeamScope = useSelector(getHasViewTeamScope);

  // Loading
  const isOverviewDataFetching = useSelector(getIsOverviewDataFetching);
  const isOverviewDataError = useSelector(getHasAggregatedOverviewFetchError);
  const isOverviewFetchSuccessful = useSelector(getIsInitialOverviewFetchSuccessful);
  const hasFetchOwnersSucceeded = useSelector(getFetchOwnersSucceeded);
  const hasFetchUsersSucceeded = useSelector(getFetchUsersSucceeded);

  // Teams, Users, and Owners
  const currentUser = useSelector(getCurrentUser);
  const ownerIdsWithPermissions = useSelector(getOwnerIdsWithPermissions);
  const ownerIdToUserIdMap = useSelector(getOwnerIdToUserIdMap);
  const owners = useSelector(getOwnersMap);
  const userHasAccessToNoForecastTypes = useSelector(getUserHasAccessToNoForecastTypes);

  // Filters
  const dealGroupingType = useSelector(getDealGroupingTypeFilter);
  const forecastCadence = useSelector(getPortalWideCadence);
  const isAllPipelines = useSelector(getIsAllPipelines);
  const isDealStageView = useSelector(getIsDealStageView);
  const pipelines = useSelector(getPipelineFilter);
  const selectedForecastIsDefault = useSelector(getSelectedForecastTypeIsDefault);
  const timePeriod = useSelector(getTimePeriodFilter);

  // Other
  const isUngatedToManualSubmissionOverhaul = useSelector(getIsUngatedToManualSubmissionOverhaul);
  const companyCurrency = useSelector(getCompanyCurrency);
  const {
    data: preferredCurrency
  } = useFetchPreferredCurrency();
  const repRowData = useSelector(getRepRowData);
  const selectedDealCountOld = useSelector(getSelectedDealCount);
  const selectedDealIdsCount = (_useSelector$length = (_useSelector = useSelector(getSelectedDealIds)) === null || _useSelector === void 0 ? void 0 : _useSelector.length) !== null && _useSelector$length !== void 0 ? _useSelector$length : 0;
  const selectedDealCount = isUngatedToManualSubmissionOverhaul ? selectedDealIdsCount : selectedDealCountOld;
  const isUser = repRowData === null || repRowData === void 0 ? void 0 : repRowData.isUser;
  const id = repRowData === null || repRowData === void 0 ? void 0 : repRowData.id;
  const ownerId = useMemo(() => getOwnerIdFromURL(location), [location]);
  const owner = useMemo(() => getPermissionedOwnerFromURL(location, ownerIdToUserIdMap, ownerIdsWithPermissions, owners), [location, ownerIdToUserIdMap, ownerIdsWithPermissions, owners]);
  const showGoalsBugAlert = useSelector(getShouldShowGoalsBugAlert);
  useEffect(() => {
    if (repRowData && !isOverviewDataFetching) {
      const hasData = typeof repRowData.forecastLastModified === 'number';
      const pipelineToTrack = isAllPipelines ? ALL_PIPELINES : pipelines.size === 1 ? pipelines.first() : MULTI_BUT_NOT_ALL_PIPELINES;
      const customTimePeriod = isTimePeriodCustom(timePeriod);
      const timePeriodToTrack = customTimePeriod ? CUSTOM_DATE_OPTION : timePeriod;
      const cadenceMatchesTimePeriod = customTimePeriod ? false : timePeriod.includes(forecastCadence);
      trackRepDetailView({
        hasData,
        isDealStageView,
        isDefaultForecast: selectedForecastIsDefault,
        pipeline: pipelineToTrack,
        timePeriod: timePeriodToTrack,
        cadence: forecastCadence,
        cadenceMatchesTimePeriod
      });
    }
  }, [repRowData, isDealStageView, selectedForecastIsDefault, isOverviewDataFetching, isAllPipelines, pipelines, timePeriod, forecastCadence]);

  // manages fetching data on initial load or ownerId change
  useEffect(() => {
    dispatch(setDetailViewOwner(ownerId));
    if (!isOverviewFetchSuccessful) {
      // note that these apis will be skipped if we come from the overview page since the data is already fetched
      dispatch(fetchInitialOverviewData()).catch(() => {
        /* handled in fetchInitialOverviewData */
      });
      Promise.all([dispatch(fetchCurrencyInfoAction()), dispatch(fetchPortalWideCadence())]).then(() => {
        dispatch(fetchAggregatedOverviewData()).catch(() => {
          /* handled in fetchAggregatedOverviewData */
        });
      }).catch(() => {});
    } else {
      dispatch(fetchAggregatedOverviewData()).catch(() => {
        /* handled in fetchAggregatedOverviewData */
      });
    }
    // need to not call this when isOverviewFetchSuccessful changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ownerId]);

  // Redirects user if they don't have permission to view the page
  useEffect(() => {
    const noViewPermission = !hasViewAllScope && !hasViewTeamScope && currentUser && owner && currentUser.user_id !== owner.get('activeUserId');
    const invalidOwner = hasFetchOwnersSucceeded && hasFetchUsersSucceeded && !owner;
    if (noViewPermission || invalidOwner) {
      history.push('/');
    }
  }, [currentUser, hasFetchOwnersSucceeded, hasFetchUsersSucceeded, hasViewAllScope, hasViewTeamScope, history, owner]);

  // Clears owner from state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(setDetailViewOwner(null));
    };
  }, [dispatch]);
  const handleSubmitForecastFromDealTable = useCallback(({
    amount,
    cadence,
    fiscalOffset,
    prevAmount,
    submittedPipelineId,
    timePeriodValue,
    isAllPipelinesSubmission
  }) => {
    dispatch(updateForecastDisplayFromEmbeddedSubmission({
      amount,
      cadence,
      fiscalOffset,
      // this is an addition
      id,
      isUser,
      prevAmount,
      submittedPipelineId,
      timePeriodValue,
      isAllPipelinesSubmission
    }));
  }, [dispatch, id, isUser]);
  const handleTrack = useMemo(() => {
    var _repRowData$totalFore, _repRowData$totalFore2;
    return createOnTrackHandlerForSubmissionPanel({
      companyCurrency,
      currencyCode: (_repRowData$totalFore = repRowData === null || repRowData === void 0 || (_repRowData$totalFore2 = repRowData.totalForecastedAmount) === null || _repRowData$totalFore2 === void 0 ? void 0 : _repRowData$totalFore2.currencyCode) !== null && _repRowData$totalFore !== void 0 ? _repRowData$totalFore : '',
      isDealStageView,
      isDetailPage: true,
      submissionPanelData: repRowData,
      isDefaultForecast: selectedForecastIsDefault,
      selectedDealCount
    });
  }, [companyCurrency, isDealStageView, repRowData, selectedForecastIsDefault, selectedDealCount]);
  const noop = useCallback(() => {}, []);
  const showCategoryForecastingSetup = dealGroupingType === FORECAST_CATEGORY && showForecastCategoryWizard;
  const showDeactivatedUserError = (owner === null || owner === void 0 || (_owner$get = owner.get) === null || _owner$get === void 0 ? void 0 : _owner$get.call(owner, 'isActive')) === false;
  if (userHasAccessToNoForecastTypes) {
    return /*#__PURE__*/_jsx(RepDetailWrapper, {
      navMarkerLabel: "DETAIL_INDEX_LOAD",
      children: /*#__PURE__*/_jsx(ForecastTypePermissionError, {
        isDetail: true
      })
    });
  }
  if (showCategoryForecastingSetup) {
    return /*#__PURE__*/_jsx(RepDetailWrapper, {
      navMarkerLabel: "DETAIL_INDEX_LOAD",
      children: /*#__PURE__*/_jsx(CategoryForecastingSetup, {
        onConfirm: fetchAggregatedOverviewData
      })
    });
  }
  if (isOverviewDataError) {
    return /*#__PURE__*/_jsx(RepDetailWrapper, {
      navMarkerLabel: "DETAIL_INDEX_OVERVIEW_LOAD_FAIL",
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        type: "badRequest",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "repOverviewTable.fetchErrorMessage"
        })
      })
    });
  }
  if (showDeactivatedUserError) {
    return /*#__PURE__*/_jsxs(RepDetailWrapper, {
      navMarkerLabel: "DETAIL_INDEX_LOAD",
      children: [/*#__PURE__*/_jsx(View, {
        marginBlockEnd: "400",
        as: "div",
        children: /*#__PURE__*/_jsx(ForecastFilters, {
          handleSelectUserOrTeam: noop,
          isDetail: true
        })
      }), /*#__PURE__*/_jsx(RepOverviewTableContainer, {
        isDetailPage: true,
        preferredCurrency: preferredCurrency,
        showDeactivatedUserError: showDeactivatedUserError
      })]
    });
  }
  return /*#__PURE__*/_jsxs(RepDetailWrapper, {
    navMarkerLabel: "DETAIL_INDEX_LOAD",
    children: [showGoalsBugAlert && /*#__PURE__*/_jsx(GoalsBugAlert, {}), showProcessingAlert && /*#__PURE__*/_jsx(ProcessingAlert, {}), /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      as: "div",
      children: /*#__PURE__*/_jsx(ForecastFilters, {
        handleSelectUserOrTeam: noop,
        isDetail: true
      })
    }), /*#__PURE__*/_jsx(RepOverviewTableContainer, {
      isDetailPage: true,
      preferredCurrency: preferredCurrency
    }), /*#__PURE__*/_jsx(DealTableFilters, {
      ownerId: ownerId,
      isDetailPage: true
    }), /*#__PURE__*/_jsx(FrameworkDealTable, {
      owner: owner
    }), /*#__PURE__*/_jsx(RepDetailForecastSubmissionPanelWrapper, {
      handleTrack: handleTrack,
      onSubmitSuccess: handleSubmitForecastFromDealTable,
      ownerId: isUser ? id : undefined,
      teamId: isUser ? undefined : id
    }), /*#__PURE__*/_jsx(CreateForecastTypeWizardWrapper, {})]
  });
};
export default ProvideReferenceResolvers({
  [DEAL_PROPERTY]: DealPropertyReferenceResolver,
  [DEAL_PIPELINE]: PipelineReferenceResolver,
  [DEAL_PIPELINE_STAGE]: PipelineStageReferenceResolver
})(RepDetailContainer);